import { Location, Viewport } from '@/types/maps'

// Haversine distance between two lat/long points, in km
export const distanceInKm = (a: Location, b: Location) => {
  const earthRadiusKm = 6371
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(b.latitude - a.latitude)
  const dLon = toRad(b.longitude - a.longitude)
  const lat1 = toRad(a.latitude)
  const lat2 = toRad(b.latitude)
  const sinDLat = Math.sin(dLat / 2)
  const sinDLon = Math.sin(dLon / 2)
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon
  return 2 * earthRadiusKm * Math.asin(Math.sqrt(h))
}

// Mercator-projected latitude, used to measure the vertical span of a bounding box in "world" units
const mercatorLatRad = (lat: number) => {
  const sin = Math.sin((lat * Math.PI) / 180)
  const radians = Math.log((1 + sin) / (1 - sin)) / 2
  return Math.max(Math.min(radians, Math.PI), -Math.PI) / 2
}

// Derives a center + zoom that frames the given points.
// Width and height should be the pixel size of the map container
export const getViewportForPoints = (
  points: Location[],
  { width, height }: { width: number; height: number },
): Viewport => {
  const minZoom = 2
  const maxZoom = 14
  const padding = 96
  const lats = points.map((point) => point.latitude)
  const lngs = points.map((point) => point.longitude)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)
  const paddedWidth = Math.max(width - padding * 2, 1)
  const paddedHeight = Math.max(height - padding * 2, 1)
  const latFraction = (mercatorLatRad(maxLat) - mercatorLatRad(minLat)) / Math.PI
  const lngDiff = maxLng - minLng
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360
  const zoomForFraction = (containerPx: number, fraction: number) =>
    fraction > 0 ? Math.log2(containerPx / 256 / fraction) : Infinity
  const zoom = Math.min(zoomForFraction(paddedHeight, latFraction), zoomForFraction(paddedWidth, lngFraction), maxZoom)

  return {
    latitude: (minLat + maxLat) / 2,
    longitude: (minLng + maxLng) / 2,
    zoom: Math.max(zoom, minZoom),
  }
}
